import React from 'react'
import { SnackbarType, useGlobalSnackbar } from './globalSnackBarContext'
import {
  IconButton,
  Snackbar,
  SnackbarOrigin,
  useMediaQuery,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTheme, styled } from '@mui/material/styles'
import { Close, CheckCircle } from '@mui/icons-material'
import SnackbarContent from '@mui/material/SnackbarContent'
import Alert from '@mui/material/Alert'
import { InfoCircleIcon } from '@src/icons'
import { useRouter } from 'next/router'
import { SrpExperience } from '@src/utils/searchUtils'
import { AppCookies, useCookies } from '@src/utils/cookies'
import getFeatureFlags from '@src/utils/getFeatureFlags'

const SuccessAlert = styled(Alert)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  '& .MuiAlert-message': {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
  '& .MuiAlert-icon': {
    color: theme.palette.common.white,
  },
  '& .MuiAlert-action': {
    color: theme.palette.common.white,
  },
}))

const useStyles = makeStyles<
  void,
  'successAlert' | 'infoAlert' | 'errorAlert' | 'warningAlert'
>()((theme, _params, classes) => ({
  snackbarMessageContent: {
    '& .MuiSnackbarContent-root': {
      maxWidth: 304,
      minWidth: 304,
    },
    '& .MuiSnackbarContent-message': {
      padding: theme.typography.pxToRem(4),
      margin: theme.typography.pxToRem(4),
      marginLeft: 0,
      paddingLeft: 0,
      marginBottom: theme.typography.pxToRem(6),
      maxWidth: 240,
      paddingBottom: theme.typography.pxToRem(6),
    },
  },
  snackbarMessageContentMobile: {
    '& .MuiSnackbarContent-root': {
      width: '100%',
      maxWidth: '93%',
      borderRadius: '5px',
      margin: theme.typography.pxToRem(6),
      paddingBottom: theme.typography.pxToRem(6),
      paddingTop: theme.typography.pxToRem(6),
      paddingLeft: theme.typography.pxToRem(16),
      paddingRight: theme.typography.pxToRem(16),
    },
  },
  anchorTag: {
    textDecoration: 'none',
  },
  actionContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.typography.pxToRem(2),
    margin: theme.typography.pxToRem(2),
  },
  verticalBar: {
    width: '1px',
    height: theme.typography.pxToRem(18),
    backgroundColor: theme.palette.common.white,
    marginRight: theme.typography.pxToRem(6),
    marginLeft: theme.typography.pxToRem(8),
  },
  actionButton: {
    paddingRight: theme.typography.pxToRem(4),
    paddingLeft: theme.typography.pxToRem(4),
    cursor: 'pointer',
    color: theme.palette.common.white,
  },
  message: {
    display: 'flex',
    marginTop: theme.spacing(1),
  },
  snackbarMessageStyling: {
    display: 'inline',
  },
  icon: {
    marginRight: theme.typography.pxToRem(6),
    marginLeft: 0,
  },
  backdrop: {
    backgroundColor: theme.palette.common.white,
    opacity: 0.9,
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1000, // https://material-ui.com/customization/z-index/
  },
  root: {
    [theme.breakpoints.down('sm')]: {
      right: 0,
      left: 0,
    },
  },
  alertRoot: {
    top: 76.8 + 24,
    [`& .${classes.successAlert}, & .${classes.infoAlert}, & .${classes.errorAlert}`]:
      {
        '& .MuiAlert-message': {
          color: theme.palette.common.white,
          fontWeight: theme.typography.fontWeightBold,
        },
        '& .MuiAlert-icon': {
          color: theme.palette.common.white,
        },
        '& .MuiAlert-action': {
          color: theme.palette.common.white,
        },
      },
    [`& .${classes.warningAlert}`]: {
      display: 'flex',
      alignItems: 'center',
      '& .MuiAlert-message': {
        color: theme.palette.common.black,
        fontWeight: theme.typography.fontWeightBold,
      },
      '& .MuiAlert-icon': {
        color: theme.palette.warning.main,
      },
      '& .MuiAlert-action': {
        color: theme.palette.primary.main,
      },
      '& a': {
        color: theme.palette.primary.main,
      },
    },
  },
  content: {
    [theme.breakpoints.down('sm')]: {
      borderRadius: 0,
      padding: theme.spacing(4, 6),
    },
  },
  snackbarError: {
    backgroundColor: theme.palette.error.main,
    '& a': {
      color: theme.palette.common.white,
      textDecoration: 'underline',
    },
  },
  successAlert: {
    backgroundColor: theme.palette.secondary.main,
  },
  infoAlert: {},
  warningAlert: {
    backgroundColor: theme.palette.common.white,
    border: `solid 1px ${theme.palette.warning.main}`,
    color: '#202020',
    borderWidth: '1px 1px 1px 5px',
  },
  errorAlert: {
    backgroundColor: theme.palette.error.main,
    '& a': {
      color: theme.palette.common.white,
      textDecoration: 'underline',
    },
  },
  snackbarSuccess: { backgroundColor: theme.palette.success.main },
  snackbarInfo: { backgroundColor: theme.palette.primary.main },
}))

const GlobalSnackbar = () => {
  const { classes, cx } = useStyles()

  const { globalSnackbarState, setGlobalSnackbarState } = useGlobalSnackbar()
  const theme = useTheme()
  // If alertSeverity exists after this block, then snackbar contains an alert
  let alertSeverity
  let isAdditionalSuccessAlert = false
  switch (globalSnackbarState.variant) {
    case SnackbarType.SuccessAlert:
      alertSeverity = 'success'
      break
    case SnackbarType.InfoAlert:
      alertSeverity = 'info'
      break
    case SnackbarType.WarningAlert:
      alertSeverity = 'warning'
      break
    case SnackbarType.ErrorAlert:
      alertSeverity = 'error'
      break
    case SnackbarType.AdditionalSuccessAlert:
      alertSeverity = 'success'
      isAdditionalSuccessAlert = true
      break
    default:
      alertSeverity = undefined
  }

  const router = useRouter()
  const srpPage = router.asPath.includes('/search/')
  const [cookies] = useCookies([AppCookies.SrpExperience])
  const srpExperience: SrpExperience =
    cookies[AppCookies.SrpExperience] || 'new'
  const newSrpEnabled =
    getFeatureFlags().newSrp && srpPage && srpExperience === 'new'

  const { dismissableByUser, actionableLink } = globalSnackbarState
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleClose = () => {
    setGlobalSnackbarState({ ...globalSnackbarState, open: false })
  }

  const getAppropriateIconForSnackbarContent = (): JSX.Element | null => {
    if (globalSnackbarState.variant === SnackbarType.Success)
      return <CheckCircle className={classes.icon} />
    if (globalSnackbarState.variant === SnackbarType.Info)
      return <InfoCircleIcon className={classes.icon} />
    return null
  }

  const getHideDuration = () => {
    if (alertSeverity) {
      return null
    }
    if (dismissableByUser || actionableLink) {
      return 10000
    }
    return 5000
  }

  const mobileClassesApplicable =
    isMobile && !alertSeverity ? classes.snackbarMessageContentMobile : ''
  const mobileClassesApplicableNewSrp =
    newSrpEnabled && isMobile && !alertSeverity
      ? classes.snackbarMessageContentMobile
      : ''

  const getToastPosition = (): SnackbarOrigin => {
    const coords: SnackbarOrigin = { vertical: 'top', horizontal: 'right' }
    if (alertSeverity) {
      coords.horizontal = 'center'
      return { ...coords }
    }
    if (isMobile) {
      if (newSrpEnabled) {
        coords.vertical = 'top'
        coords.horizontal = 'center'
      } else {
        coords.vertical = 'bottom'
        coords.horizontal = 'center'
      }
      return { ...coords }
    }
    return { ...coords }
  }

  const { vertical, horizontal } = getToastPosition()

  return (
    <>
      {globalSnackbarState.disableWindowBlurListener &&
        globalSnackbarState.open && <div className={classes.backdrop} />}
      <Snackbar
        anchorOrigin={{ vertical, horizontal }}
        open={globalSnackbarState.open}
        autoHideDuration={getHideDuration()}
        onClose={handleClose}
        classes={{
          root: alertSeverity ? classes.alertRoot : classes.root,
          anchorOriginTopRight: classes.snackbarMessageContent,
          anchorOriginBottomCenter: mobileClassesApplicable,
          anchorOriginTopCenter: mobileClassesApplicableNewSrp,
        }}
        data-testid="global-snackbar"
        id="global-snackbar"
      >
        {alertSeverity ? (
          isAdditionalSuccessAlert ? (
            <SuccessAlert
              severity={alertSeverity}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleClose}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
            >
              {globalSnackbarState.message}
            </SuccessAlert>
          ) : (
            <Alert
              classes={{
                standardSuccess: classes.successAlert,
                standardInfo: classes.infoAlert,
                standardWarning: classes.warningAlert,
                standardError: classes.errorAlert,
              }}
              severity={alertSeverity}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={handleClose}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
            >
              {globalSnackbarState.message}
            </Alert>
          )
        ) : (
          <SnackbarContent
            className={cx(
              classes.content,
              classes[globalSnackbarState.variant || ''],
              actionableLink || dismissableByUser
                ? classes.snackbarMessageContent
                : ''
            )}
            message={
              <span className={classes.message}>
                {getAppropriateIconForSnackbarContent()}
                <span className={classes.snackbarMessageStyling}>
                  {globalSnackbarState.message}
                </span>
              </span>
            }
            action={
              <div>
                <div className={classes.actionContainer}>
                  {actionableLink && (
                    <a href={actionableLink} className={classes.anchorTag}>
                      <div
                        key="action"
                        color="inherit"
                        onClick={handleClose}
                        className={classes.actionButton}
                        role="button"
                        tabIndex={0}
                      >
                        Action
                      </div>
                    </a>
                  )}
                  {actionableLink && dismissableByUser && (
                    <div className={classes.verticalBar}></div>
                  )}
                  {dismissableByUser && (
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={handleClose}
                    >
                      <Close fontSize="inherit" />
                    </IconButton>
                  )}
                </div>
              </div>
            }
          />
        )}
      </Snackbar>
    </>
  )
}

export default GlobalSnackbar
